// TODO: after migration consider https://www.npmjs.com/package/analytics

import { info } from './logger';
import pick from './pick';
// TODO to move analytics to its own package so can consume from api and core without a circular dependency
import { triggerEmailUserAnalyticsEvent } from '../../api/emailUsers/emailUsers';
import mixpanel from 'mixpanel-browser';
export const mixPanelToken = process.env.MIXPANEL_TOKEN;
export interface IAnalyticsProps {
  [key: string]: string;
}

export interface IWindowWithAnalytics extends Window {
  dataLayer: GTMDataLayer;
  ga(functionName: string, hitType: string, eventName: string, eventAction: string, eventLabel: string): void;
  FS: FullStoryAnalytics;
}

declare const window: IWindowWithAnalytics;

/**
 * Fires an event to multiple services.
 * Please use the gaLabelKeysFunc and gaActionKeysFunc going forward so that we can deprecate GTM usage.
 * See https://developers.google.com/analytics/devguides/collection/analyticsjs/sending-hits and https://support.google.com/analytics/answer/1033068?hl=en for info on sending GA events.
 *
 * @param eventName
 * @param eventData a flat dictionary of event data (string key/value pairs)
 * @param gaActionKeysFunc picks keys from eventData to provide to GA as the action property. If provided, no event will be fired to GTM. An action should always be provided.
 * @param gaLabelKeysFunc picks keys from eventData to provide to GA as the label property. If provided, no event will be fired to GTM. A label is optional but recommended.
 * @param userEmail send the event to Vero for the user matching this email address.
 */
export function fireEvent(
  eventName: string,
  eventData: IAnalyticsProps,
  gaActionKeysFunc?: (eventDataKeys: string[]) => string[],
  gaLabelKeysFunc?: (eventDataKeys: string[]) => string[],
  userEmail?: string
) {
  info('Fire analytics event: ', eventName, eventData);

  if (!gaLabelKeysFunc && !gaActionKeysFunc) {
    info('Legacy GTM event', eventName);
    try {
      // Google Tag Manager
      window.dataLayer &&
        window.dataLayer.push({
          event: eventName,
          ...eventData,
        });
    } catch (error) {
      console.error(`Failed to send GTM event ${eventName}`, error);
    }
  } else {
    const eventKeys = Object.keys(eventData);
    const gaSerialize = (keys: string[]) => (keys?.length ? JSON.stringify(pick(eventData, keys)) : '');
    const gaLabel = gaSerialize(gaLabelKeysFunc?.(eventKeys));
    const gaAction = gaSerialize(gaActionKeysFunc?.(eventKeys));
    info('GA event', eventName, 'label', gaLabel, 'action', gaAction);
    window.ga && window.ga('send', 'event', eventName, gaAction, gaLabel);
  }

  try {
    // Mixpanel
    if (mixPanelToken) {
      console.log('Firing mixpanel event');
      mixpanel.track(eventName, { ...eventData });
    }
  } catch (error) {
    console.error(`Failed to send MixPanel event ${eventName}`, error);
  }

  try {
    // Fullstory requires all event keys to be suffixed with their type
    // see https://help.fullstory.com/hc/en-us/articles/360020623234
    const fullStoryEventData: { [key: string]: string } = {};
    for (const key in eventData) {
      fullStoryEventData[key + '_str'] = eventData[key];
    }
    window.FS && window.FS.event(eventName, fullStoryEventData);
  } catch (error) {
    console.error(`Failed to send Fullstory event ${eventName}`, error);
  }

  if (userEmail) {
    try {
      triggerEmailUserAnalyticsEvent({
        userEmail,
        eventName,
        eventParameters: eventData,
      });
    } catch (error) {
      console.error(`Failed to triggers event ${eventName}`, error);
    }
  }
}

export function fireShareEvent(shareLabel: string) {
  fireEvent('Share', {
    shareAction: 'GiveInkind',
    shareLabel,
  });
}

export function fireDonateEvent(shareLabel: string) {
  fireEvent('Donate', {
    shareAction: 'GiveInkind',
    shareLabel,
  });
}
